/**
 * assets/customizer-clothes.js
 * Clothes-specific customization logic.
 */
class ClothesCustomizer extends ColorCustomizer {
  constructor(config) {
    super(config);
    window.selectedClothesStyle = '';
    window.selectedClothesColor = '';
    window.selectedClothesSize = 'M';
    this.hideAdultSizesOnProduction = false; // Toggle to false on staging/testing theme to show adult sizes
  }

  get selectedClothesStyle() { return window.selectedClothesStyle; }
  set selectedClothesStyle(val) { window.selectedClothesStyle = val; }

  get selectedClothesColor() { return window.selectedClothesColor; }
  set selectedClothesColor(val) { window.selectedClothesColor = val; }

  get selectedClothesSize() { return window.selectedClothesSize; }
  set selectedClothesSize(val) { window.selectedClothesSize = val; }

  bindGlobalMethods() {
    super.bindGlobalMethods();
    window.getClothesOptions = () => this.getClothesOptions();
    window.renderClothesPicker = () => this.renderClothesPicker();
    window.selectClothesStyle = (style) => this.selectClothesStyle(style);
    window.selectClothesColor = (color) => this.selectClothesColor(color);
    window.selectClothesSize = (size) => this.selectClothesSize(size);
    window.syncClothesVariant = () => this.syncClothesVariant();
    
    window.openSizeGuideModal = () => this.openSizeGuideModal();
    window.toggleSizeUnit = (unit) => this.toggleSizeUnit(unit);
    window.closeSizeGuideModal = (e) => this.closeSizeGuideModal(e);
    window.closeSizeGuideModalBtn = () => this.closeSizeGuideModalBtn();
  }

  getClothesOptions() {
    const styles = new Set();
    const colors = new Set();
    const sizes = new Set();
    
    window.productVariants.forEach(v => {
      const parsed = window.parseVariantTitle(v.title);
      if (parsed.style) styles.add(parsed.style);
      if (parsed.color) colors.add(parsed.color.toLowerCase());
      if (parsed.size) sizes.add(parsed.size);
    });
    
    return {
      styles: Array.from(styles),
      colors: Array.from(colors),
      sizes: Array.from(sizes)
    };
  }

  renderClothesPicker() {
    const container = document.querySelector('.clothes-variant-selectors');
    if (!container) return;
    
    const { styles } = this.getClothesOptions();
    
    // Filter sizes and colors to only those available for the selected style
    const availableVariants = window.productVariants.filter(v => {
      const parsed = window.parseVariantTitle(v.title);
      return parsed.style === this.selectedClothesStyle;
    });

    let activeSizes = Array.from(new Set(availableVariants.map(v => window.parseVariantTitle(v.title).size).filter(Boolean)));
    const isDTF = this.selectedClothesStyle && this.selectedClothesStyle.toLowerCase().includes('dtf');
    if (this.hideAdultSizesOnProduction && window.isClothesToddler && !isDTF) {
      const isKidsSize = (size) => {
        const s = size.toUpperCase();
        return s.endsWith('T') || s.startsWith('Y') || /^\d+$/.test(s);
      };
      activeSizes = activeSizes.filter(s => isKidsSize(s));
    }
    const activeColors = Array.from(new Set(availableVariants.map(v => window.parseVariantTitle(v.title).color).filter(Boolean)));
    
    const defaultColor = window.isClothesToddler ? 'white' : 'pink';
    const clothesColorOrder = [defaultColor, ...['pink', 'white', 'black', 'blue', 'dusty blue', 'heather yellow gold', 'heather red', 'navy', 'gray', 'grey', 'green', 'sage', 'yellow', 'red', 'oatmeal', 'beige', 'purple'].filter(c => c !== defaultColor)];
    activeColors.sort((a, b) => {
      let indexA = clothesColorOrder.indexOf(a.toLowerCase());
      let indexB = clothesColorOrder.indexOf(b.toLowerCase());
      if (indexA === -1) indexA = 99;
      if (indexB === -1) indexB = 99;
      return indexA - indexB;
    });

    const activeColorDetails = this.colorDetails[this.selectedClothesColor] || { name: this.selectedClothesColor };
    const activeName = activeColorDetails.name;

    const selectedColorText = document.getElementById('clothes-selected-color-text');
    if (selectedColorText) {
      selectedColorText.innerText = activeName;
    }
    
    const styleContainer = document.getElementById('clothes-style-buttons');
    if (styleContainer) {
      styleContainer.innerHTML = styles.map(style => {
        const isActive = this.selectedClothesStyle === style;
        
        // Find min and max price for variants of this style
        const styleVariants = window.productVariants.filter(v => {
          const parsed = window.parseVariantTitle(v.title);
          return parsed.style === style;
        });
        
        const isStyleSoldOut = styleVariants.length > 0 && styleVariants.every(v => !v.available);
        
        let priceStr = '';
        if (styleVariants.length > 0) {
          const prices = styleVariants.map(v => v.price);
          const minPrice = Math.min(...prices);
          const maxPrice = Math.max(...prices);
          if (minPrice === maxPrice) {
            priceStr = window.formatMoney(minPrice);
          } else {
            priceStr = `${window.formatMoney(minPrice)} - ${window.formatMoney(maxPrice)}`;
          }
        }
        
        return `
          <button type="button" class="variant-option__button-label ${isActive ? 'active-style' : ''} ${isStyleSoldOut ? 'sold-out-slashed' : ''}" 
                  onclick="window.selectClothesStyle('${style}')"
                  style="flex: 1 1 calc(33.33% - 10px); min-width: calc(33.33% - 10px); padding: 12px 6px; min-height: 72px; border: 1px solid ${isActive ? 'var(--color-foreground)' : '#e5e7eb'}; border-radius: 40px; background-color: ${isActive ? 'rgb(var(--color-foreground-rgb))' : '#fff'}; color: ${isActive ? 'var(--color-background)' : 'var(--color-foreground)'}; cursor: pointer; transition: all 0.2s; text-align: center; font-size: 0.95rem; font-weight: 500;">
            <span class="variant-option__button-title" style="display: block; font-weight: 500; font-size: 0.95rem;">${style}${isStyleSoldOut ? ' (Sold Out)' : ''}</span>
            <span class="variant-option__button-price" style="font-size: 0.85rem; font-weight: 600; opacity: 0.75; display: block; margin-top: 4px;">${priceStr}</span>
          </button>
        `;
      }).join('');
    }
    
    const colorFieldset = container.querySelector('.clothes-color-option');
    if (colorFieldset) {
      const isDTF = this.selectedClothesStyle && this.selectedClothesStyle.toLowerCase().includes('dtf transfer sheet');
      const hasColors = activeColors.length > 0 && this.selectedClothesStyle && !this.selectedClothesStyle.toLowerCase().includes('digital') && !isDTF;
      colorFieldset.style.display = hasColors ? '' : 'none';
      
      if (hasColors) {
        const colorContainer = document.getElementById('clothes-color-swatches');
        if (colorContainer) {
          colorContainer.innerHTML = activeColors.map(color => {
            const isActive = this.selectedClothesColor === color;
            const details = this.colorDetails[color] || { name: color, hex: '#ccc', border: '#ccc' };
            
            // Check if this color is sold out for the selected style & size
            const matchVarForSize = window.productVariants.find(v => {
              const parsed = window.parseVariantTitle(v.title);
              return parsed.style === this.selectedClothesStyle && 
                     parsed.color === color && 
                     parsed.size === this.selectedClothesSize;
            });
            const isColorSoldOut = matchVarForSize ? !matchVarForSize.available : true;

            return `
              <div class="color-swatch-item ${isActive ? 'active' : ''} ${isColorSoldOut ? 'sold-out-slashed' : ''}" 
                   onclick="window.selectClothesColor('${color}')"
                   onmouseover="const el = document.getElementById('clothes-selected-color-text'); if(el) el.innerText = '${details.name}';"
                   onmouseout="const el = document.getElementById('clothes-selected-color-text'); if(el) el.innerText = '${activeName}';"
                   style="display: flex; align-items: center; justify-content: center; cursor: pointer; position: relative;"
                   title="${details.name}${isColorSoldOut ? ' (Sold Out)' : ''}">
                <div class="color-swatch-circle" style="width: 28px; height: 28px; border-radius: 50%; background: ${details.hex}; border: 1px solid ${details.border}; position: relative; ${isActive ? 'box-shadow: 0 0 0 2px #fff, 0 0 0 3px #6e8efb;' : ''}">
                  ${isActive ? '<div style="position: absolute; top: -2px; right: -2px; background: #6e8efb; color: white; border-radius: 50%; width: 12px; height: 12px; display: flex; align-items: center; justify-content: center; font-size: 7px; border: 1px solid #fff;">✓</div>' : ''}
                </div>
              </div>
            `;
          }).join('');
        }
      }
    }
    
    const sizeFieldset = container.querySelector('.clothes-size-option');
    if (sizeFieldset) {
      const isDTF = this.selectedClothesStyle && this.selectedClothesStyle.toLowerCase().includes('dtf transfer sheet');
      const hasSizes = activeSizes.length > 0 && this.selectedClothesStyle && !this.selectedClothesStyle.toLowerCase().includes('digital') && (!isDTF || activeSizes.length > 1);
      sizeFieldset.style.display = hasSizes ? '' : 'none';
      
      if (hasSizes) {
        const sizeContainer = document.getElementById('clothes-size-buttons');
        if (sizeContainer) {
          const renderButtonHtml = (size) => {
            const isActive = this.selectedClothesSize === size;
            
            // Check if this size is sold out for the selected style & color
            const matchVar = availableVariants.find(v => {
              const parsed = window.parseVariantTitle(v.title);
              if (isDTF) return parsed.size === size;
              return parsed.color === this.selectedClothesColor && parsed.size === size;
            });
            const isSizeSoldOut = matchVar ? !matchVar.available : true;
            
            if (isDTF) {
              const showPrice = !window.isClothesToddler;
              const priceStr = matchVar ? window.formatMoney(matchVar.price) : '';
              
              let titleLine1 = size;
              let titleLine2 = '';
              if (window.isClothesToddler) {
                const parenIndex = size.indexOf('(');
                if (parenIndex !== -1) {
                  titleLine1 = size.substring(0, parenIndex).trim();
                  titleLine2 = size.substring(parenIndex).trim();
                }
              }
              
              const buttonStyle = window.isClothesToddler
                ? `flex: 1 1 0 !important; min-width: 0 !important; padding: 10px 4px !important; min-height: 58px !important; border: 1px solid ${isActive ? 'var(--color-foreground)' : '#e5e7eb'} !important; border-radius: var(--style-border-radius-buttons-primary, 20px) !important; background-color: ${isActive ? 'rgb(var(--color-foreground-rgb))' : '#fff'} !important; color: ${isActive ? 'var(--color-background)' : 'var(--color-foreground)'} !important; cursor: pointer; transition: all 0.2s; text-align: center; display: flex; flex-direction: column; justify-content: center; align-items: center;`
                : `flex: 1 1 calc(50% - 10px) !important; min-width: calc(50% - 10px) !important; padding: 12px 6px !important; min-height: 72px !important; border: 1px solid ${isActive ? 'var(--color-foreground)' : '#e5e7eb'} !important; border-radius: var(--style-border-radius-buttons-primary, 40px) !important; background-color: ${isActive ? 'rgb(var(--color-foreground-rgb))' : '#fff'} !important; color: ${isActive ? 'var(--color-background)' : 'var(--color-foreground)'} !important; cursor: pointer; transition: all 0.2s; text-align: center; font-size: 0.95rem; font-weight: 500;`;
              
              const titleStyle = window.isClothesToddler
                ? 'font-weight: 600; font-size: 0.9rem;'
                : 'font-weight: 500; font-size: 0.95rem;';
              
              return `
                <button type="button" class="variant-option__button-label ${isActive ? 'active-style' : ''} ${isSizeSoldOut ? 'sold-out-slashed' : ''}" 
                        onclick="window.selectClothesSize('${size}')"
                        style="${buttonStyle}">
                  <span class="variant-option__button-title" style="display: block; ${titleStyle} line-height: 1.2;">${titleLine1}${isSizeSoldOut ? ' (Sold Out)' : ''}</span>
                  ${titleLine2 ? `<span class="variant-option__button-subtitle" style="display: block; font-size: 0.8rem; font-weight: 600; margin-top: 4px; line-height: 1.2;">${titleLine2}</span>` : ''}
                  ${showPrice ? `<span class="variant-option__button-price" style="font-size: 0.85rem; font-weight: 600; opacity: 0.75; display: block; margin-top: 4px;">${priceStr}</span>` : ''}
                </button>
              `;
            } else {
              return `
                <button type="button" 
                        onclick="window.selectClothesSize('${size}')"
                        class="${isSizeSoldOut ? 'sold-out-slashed' : ''}"
                        style="border: 1px solid ${isActive ? 'var(--color-foreground)' : '#e5e7eb'}; background-color: ${isActive ? 'rgb(var(--color-foreground-rgb))' : '#fff'}; color: ${isActive ? 'var(--color-background)' : 'var(--color-foreground)'}; cursor: pointer; transition: all 0.2s; font-size: 0.9rem; font-weight: 600; display: flex; align-items: center; justify-content: center; width: 44px; height: 44px; border-radius: 50%; padding: 0; position: relative;">
                  ${size}
                </button>
              `;
            }
          };

          const isKidsSize = (size) => {
            const s = size.toUpperCase();
            return s.endsWith('T') || s.startsWith('Y') || /^\d+$/.test(s);
          };

          const kidsSizes = activeSizes.filter(s => isKidsSize(s));
          const adultSizes = activeSizes.filter(s => !isKidsSize(s));
          const isDTF = this.selectedClothesStyle && this.selectedClothesStyle.toLowerCase().includes('dtf');

          if (!isDTF && kidsSizes.length > 0 && adultSizes.length > 0) {
            const renderGroup = (groupSizes, label, isFirst = false) => {
              return `
                <div class="clothes-size-group" style="width: 100%; margin-top: ${isFirst ? '8px' : '20px'};">
                  <div class="clothes-size-group-title" style="font-size: 0.78rem; color: #6b7280; font-weight: 400; text-transform: uppercase; margin-bottom: 8px; letter-spacing: 0.05em; line-height: 1; margin-left: 2px;">${label}</div>
                  <div style="display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 4px;">
                    ${groupSizes.map(size => renderButtonHtml(size)).join('')}
                  </div>
                </div>
              `;
            };
            sizeContainer.style.display = 'block';
            sizeContainer.innerHTML = `
              ${renderGroup(kidsSizes, 'Kids', true)}
              ${renderGroup(adultSizes, 'Adults', false)}
            `;
          } else {
            sizeContainer.style.display = 'flex';
            sizeContainer.innerHTML = activeSizes.map(size => renderButtonHtml(size)).join('');
          }
        }
      }
    }
  }

  selectClothesStyle(style) {
    this.selectedClothesStyle = style;
    this.syncClothesVariant();
    this.renderClothesPicker();
  }

  selectClothesColor(color) {
    this.selectedClothesColor = color;
    
    const customization = this.allCustomizations[this.selectedCustomizationIndex];
    if (customization && customization.key) {
      let gKey = customization.key;
      const lastDashIndex = gKey.lastIndexOf('-');
      if (lastDashIndex !== -1) {
        gKey = gKey.substring(0, lastDashIndex);
      }
      window.updateProductColor(color, gKey);
    }
    
    this.syncClothesVariant();
    this.renderClothesPicker();
  }

  selectClothesSize(size) {
    this.selectedClothesSize = size;
    this.syncClothesVariant();
    this.renderClothesPicker();
  }

  syncClothesVariant() {
    const isDigital = this.selectedClothesStyle && this.selectedClothesStyle.toLowerCase().includes('digital');
    const isDTF = this.selectedClothesStyle && this.selectedClothesStyle.toLowerCase().includes('dtf transfer sheet');
    
    const matchVar = window.productVariants.find(v => {
      const parsed = window.parseVariantTitle(v.title);
      if (parsed.style !== this.selectedClothesStyle) return false;
      if (isDigital) return true;
      if (isDTF) return parsed.size === this.selectedClothesSize;
      
      return parsed.color === this.selectedClothesColor && parsed.size === this.selectedClothesSize;
    }) || window.productVariants.find(v => {
      const parsed = window.parseVariantTitle(v.title);
      if (parsed.style !== this.selectedClothesStyle) return false;
      if (isDigital) return true;
      if (isDTF) return true;
      return parsed.color === this.selectedClothesColor;
    }) || window.productVariants.find(v => {
      return window.parseVariantTitle(v.title).style === this.selectedClothesStyle;
    });
    
    if (matchVar) {
      const parsed = window.parseVariantTitle(matchVar.title);
      if (parsed.size && parsed.size !== this.selectedClothesSize) {
        this.selectedClothesSize = parsed.size;
      }
      if (parsed.color && parsed.color !== this.selectedClothesColor) {
        this.selectedClothesColor = parsed.color;
      }
      
      const hiddenInput = document.getElementById('clothes-hidden-variant-input');
      if (hiddenInput) {
        hiddenInput.value = matchVar.id;
      }
      
      const mainProductForm = document.querySelector('form[action$="/cart/add"]');
      if (mainProductForm) {
        const mainVariantInput = mainProductForm.querySelector('input[name="id"]');
        if (mainVariantInput) {
          mainVariantInput.value = matchVar.id;
        }
      }
      
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', matchVar.id);
        window.history.replaceState({}, '', url.toString());
      } catch (e) {
        console.warn("Could not update variant URL param", e);
      }
      
      const customization = this.allCustomizations[this.selectedCustomizationIndex];
      const parsedStyle = parsed.style ? parsed.style.toLowerCase() : '';
      const isDTFOrDigital = parsedStyle.includes('dtf transfer sheet') || parsedStyle.includes('digital');

      if (customization && !isDTFOrDigital) {
        let gKey = customization.key;
        const lastDashIndex = gKey.lastIndexOf('-');
        if (lastDashIndex !== -1) {
          gKey = gKey.substring(0, lastDashIndex);
        }
        
        // Update all items in this design set locally and update DOM in-place to prevent jank
        this.allCustomizations.forEach((item, idx) => {
          if (item.key === gKey || (item.key && item.key.startsWith(gKey + '-'))) {
            item.variantId = matchVar.id;
            if (window.getUpdatedProductUrl) {
              item.imageUrl = window.getUpdatedProductUrl(item.imageUrl, matchVar.id);
            }
            
            const card = document.querySelector(`.custom-item-card[data-index="${idx}"]`);
            if (card) {
              const img = card.querySelector('.item-thumb-container img');
              if (img) img.src = item.imageUrl;
              const thumb = card.querySelector('.item-thumb-container');
              if (thumb) thumb.setAttribute('onclick', `window.openImageZoom('${item.imageUrl}')`);
            }
          }
        });
        
        if (this.updateRemoteHistoryItem) {
          this.updateRemoteHistoryItem(gKey, { variantId: matchVar.id });
        }
        
        if (window.updateBuyButtonVisibility) {
          window.updateBuyButtonVisibility();
        }
      }
      
      if (window.updateLivePrice) window.updateLivePrice();
    }
  }  openSizeGuideModal() {
    const modal = document.getElementById('size-guide-modal');
    if (modal) {
      const container = document.getElementById('size-guide-table-container');
      if (container) {
        const isClothesToddler = window.isClothesToddler;
        const isDTF = this.selectedClothesStyle && this.selectedClothesStyle.toLowerCase().includes('dtf');
        const unitToggle = document.getElementById('size-unit-toggle-wrapper');
        if (unitToggle) {
          unitToggle.style.display = isDTF ? 'none' : 'flex';
        }
        const toddlerDtfToggle = document.getElementById('toddler-dtf-mode-toggle-wrapper');
        if (toddlerDtfToggle) {
          toddlerDtfToggle.style.display = (isClothesToddler && isDTF) ? 'flex' : 'none';
        }
        
        // Category Toggle Switch Visibility
        const categoryToggle = document.getElementById('size-category-toggle-wrapper');
        if (categoryToggle) {
          categoryToggle.style.display = (isClothesToddler && !isDTF) ? 'flex' : 'none';
        }
        
        // Reset toggle styling to kids
        const kidsBtn = document.getElementById('size-category-kids');
        const adultsBtn = document.getElementById('size-category-adults');
        if (kidsBtn && adultsBtn) {
          kidsBtn.style.backgroundColor = '#fff';
          kidsBtn.style.color = '#111';
          kidsBtn.style.fontWeight = '600';
          kidsBtn.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
          
          adultsBtn.style.backgroundColor = 'transparent';
          adultsBtn.style.color = '#666';
          adultsBtn.style.fontWeight = '500';
          adultsBtn.style.boxShadow = 'none';
        }
        
        container.innerHTML = window.getApparelSizeGuideHtml(isClothesToddler, this.selectedClothesStyle, 'kids');
        
        if (isClothesToddler && isDTF && window.toggleToddlerDtfSizeMode) {
          window.toggleToddlerDtfSizeMode('landscape');
        }
      }
      modal.style.display = 'flex';
      this.lockScreen();
      this.toggleSizeUnit('inch');
    }
  }

  closeSizeGuideModal(event) {
    if (event.target.id === 'size-guide-modal') {
      this.closeSizeGuideModalBtn();
    }
  }

  closeSizeGuideModalBtn() {
    const modal = document.getElementById('size-guide-modal');
    if (modal) {
      modal.style.display = 'none';
      this.unlockScreen();
    }
  }

  toggleSizeUnit(unit) {
    const inchRows = document.querySelectorAll('.size-guide-row-inch');
    const cmRows = document.querySelectorAll('.size-guide-row-cm');
    const dropdown = document.getElementById('size-unit-dropdown');
    
    if (dropdown) {
      dropdown.value = unit;
    }
    
    if (unit === 'inch') {
      inchRows.forEach(row => row.style.display = '');
      cmRows.forEach(row => row.style.display = 'none');
    } else {
      inchRows.forEach(row => row.style.display = 'none');
      cmRows.forEach(row => row.style.display = '');
    }
  }

  updateColorVariants() {
    const customization = this.allCustomizations[this.selectedCustomizationIndex];
    
    let defaultId = window.isClothesToddler ? "48146372559038" : "46556032532670";
    let targetId = customization ? (customization.variantId || defaultId) : defaultId;
    
    const activeVar = window.productVariants.find(v => String(v.id) === String(targetId)) || window.productVariants[0];
    if (activeVar) {
      const parsed = window.parseVariantTitle(activeVar.title);
      this.selectedClothesStyle = parsed.style || '';
      this.selectedClothesColor = parsed.color || (window.isClothesToddler ? 'white' : 'pink');
      this.selectedClothesSize = parsed.size || 'M';
    }
    this.renderClothesPicker();
    if (customization) {
      window.filterVariants([targetId]);
    } else {
      window.resetVariantVisibility();
    }
  }
}
